import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/images/logo.svg';
import backgroundImage from '../assets/images/background.png';
import Input from './ui/Input';
import Button from './ui/Button';

const Login = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const { login, clearError, isLoading: authLoading } = useAuth();
  const history = useHistory();

  useEffect(() => {
    // Check for URL parameters to show specific error messages
    const urlParams = new URLSearchParams(window.location.search);
    const reason = urlParams.get('reason');
    
    if (reason === 'desk_session_ended') {
      setFieldErrors({
        password: 'Votre session de caisse a été terminée. Veuillez vous reconnecter.'
      });
    }
    if (reason === 'mfa_expired') {
      setFieldErrors({
        password: 'Votre session a expiré. Veuillez vous reconnecter pour configurer l\'authentification.'
      });
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value,
    }));
    // Only clear field error if user has typed something meaningful
    if (fieldErrors[name] && value.length > 0) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    
    // Clear previous errors
    setFieldErrors({});
    clearError(); // Clear global error

    try {
      const result = await login(credentials);

      if (result.success && result.mfaRequired) {
        const path = result.nextStep === 'enroll' ? '/mfa/enroll' : '/mfa/verify';
        history.push(path, { mfaToken: result.mfaToken });
        return;
      }

      if (result.success) {
        history.push('/');
      } else {
        
        // Handle specific backend error messages
        if (result.error && result.error.toLowerCase().includes('password')) {
          setFieldErrors({
            password: 'L\'email ou le mot de passe que vous avez saisi est incorrect.'
          });
        } else if (result.error && result.error.toLowerCase().includes('username')) {
          setFieldErrors({
            username: 'L\'email ou le mot de passe que vous avez saisi est incorrect.'
          });
        } else {
          setFieldErrors({
            password: 'Une erreur s\'est produite lors du processus de connexion. Veuillez réessayer.'
          });
        }
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      // Set a generic error
      setFieldErrors({
        password: 'Une erreur s\'est produite. Veuillez réessayer.'
      });
    } finally {
      setIsLoading(false);
    }
    
    return false; // Prevent form submission
  };

  return (
    <div className="relative flex items-center justify-center h-screen bg-[#F8F8F8]">
      {/* Background Image - exactly like gray_react */}
      <div 
        className="absolute inset-0 w-full h-full bg-cover bg-center"
        style={{ 
          backgroundImage: `url(${backgroundImage})`, 
          backgroundSize: "cover", 
          backgroundPosition: "center"
        }}
      />
      
      {/* Low opacity rectangle at the top */}
      <div 
        className="absolute top-[-83px] left-[-17px] w-[100vw] h-[193px] opacity-[0.11] blur-[50px] z-[1]"
        style={{
          background: '#0985CF',
        }}
      />
      
      <div className="relative w-[90%] sm:w-[70%] md:w-[50%] lg:w-[40%] xl:w-[35%] max-w-[600px] z-10 text-center px-4 sm:px-0">
        {/* Logo */}
        <img src={logo} alt="Logo" className="block mx-auto w-[200px] sm:w-[250px] mb-8 mt-4" />

        {/* Login Title and Description */}
        <div className="text-left">
          <h2 className="text-[24px] sm:text-[28px] md:text-[32px] font-semibold text-[#38485C] mb-2">
            Se Connecter
          </h2>
          
          <p className="text-[#38485C] font-medium text-[14px] sm:text-[16px] md:text-[18px] mt-2 mb-10">
            Saisissez vos identifiants pour accéder à l'application
          </p>
        </div>


        <form className="w-full" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          {/* Username Field */}
          <Input
            label="Nom d'utilisateur"
            name="username"
            value={credentials.username}
            onChange={handleChange}
            disabled={isLoading}
            required
            placeholder="Nom d'utilisateur"
            autoFocus
            error={fieldErrors.username}
          />
          
          {/* Password Field */}
          <Input
            label="Mot de passe"
            name="password"
            type="password"
            value={credentials.password}
            onChange={handleChange}
            disabled={isLoading}
            required
            placeholder="Mot de passe"
            error={fieldErrors.password}
          />


          {/* Login Button */}
          <Button
            type="submit"
            fullWidth
            loading={isLoading || authLoading}
            disabled={isLoading || authLoading}
          >
            Se connecter
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Login;

